import { Fragment, useMemo, useState } from 'react';
import { X, Plus, Trash2, RotateCcw, Users, Filter as FilterIcon, FileEdit, ListTree, Download, Upload, ChevronRight, ChevronDown } from 'lucide-react';
import { adminStore } from '../lib/admin';
import { db } from '../lib/db';
import { applyExclusions } from '../lib/analytics';
import { formatDate } from '../lib/utils';
import { JsonTree } from './JsonTree';
import type { AnalyticsEvent, ExclusionRule } from '../types';

type Tab = 'users' | 'filters' | 'overrides' | 'events' | 'backup';

// Friendly presets for property-based filters, mapped to the system
// properties retained in the local event cache.
const PROPERTY_PRESETS: Array<{ group: string; options: Array<[string, string]> }> = [
  {
    group: 'Location',
    options: [
      ['$geoip_country_name', 'Country'],
      ['$geoip_country_code', 'Country code'],
      ['$geoip_region', 'Region / State'],
      ['$geoip_city', 'City'],
      ['$geoip_postal_code', 'Postal code'],
      ['$geoip_time_zone', 'Geo timezone'],
    ],
  },
  {
    group: 'Network',
    options: [
      ['$ip', 'IP address'],
      ['$host', 'Page host'],
      ['$referring_domain', 'Referring domain'],
      ['$pathname', 'Page path'],
    ],
  },
  {
    group: 'Device',
    options: [
      ['$browser', 'Browser'],
      ['$os', 'Operating system'],
      ['$device_type', 'Device type'],
    ],
  },
  {
    group: 'Commerce',
    options: [
      ['quote_id', 'Quote ID'],
      ['seller_code', 'Seller code'],
      ['customer_type', 'Customer type'],
      ['search_query', 'Search query'],
      ['page_url', 'Page URL'],
    ],
  },
  {
    group: 'Session',
    options: [
      ['$session_id', 'Session ID'],
    ],
  },
];

interface AdminPanelProps {
  events: AnalyticsEvent[];
  onClose: () => void;
  onChanged: () => void;
}

export function AdminPanel({ events, onClose, onChanged }: AdminPanelProps) {
  const [tab, setTab] = useState<Tab>('users');

  const tabs: Array<{ key: Tab; label: string; icon: React.ElementType }> = [
    { key: 'users', label: 'User Aliases', icon: Users },
    { key: 'filters', label: 'Activity Filters', icon: FilterIcon },
    { key: 'overrides', label: 'Overrides', icon: FileEdit },
    { key: 'events', label: 'Raw Events', icon: ListTree },
    { key: 'backup', label: 'Import / Export', icon: Download },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-4 px-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[calc(100vh-4rem)] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-oem-200 bg-oem-navy">
          <h2 className="font-display text-lg text-white">Data Administration</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-oem-300 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex border-b border-oem-200 bg-oem-50 px-4">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? 'border-accent-500 text-oem-navy'
                  : 'border-transparent text-oem-muted hover:text-oem-700'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'users' && <AliasesTab onChanged={onChanged} />}
          {tab === 'filters' && <ExclusionsTab events={events} onChanged={onChanged} />}
          {tab === 'overrides' && <OverridesTab onChanged={onChanged} />}
          {tab === 'events' && <RawEventsTab events={events} onChanged={onChanged} />}
          {tab === 'backup' && <BackupTab onChanged={onChanged} />}
        </div>
      </div>
    </div>
  );
}

/* ── User Aliases ── */

function AliasesTab({ onChanged }: { onChanged: () => void }) {
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const aliases = adminStore.getAliases();
  const entries = Object.entries(aliases);

  const add = () => {
    if (!userId.trim() || !name.trim()) return;
    adminStore.setAlias(userId, name);
    setUserId('');
    setName('');
    onChanged();
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-oem-muted">
        Rename a user globally — the display name applies to every order, event, and report for that user id.
        Example: <span className="font-mono">10</span> → <span className="font-medium">Acme Hospital</span>
      </p>

      <div className="flex gap-2">
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="User ID (e.g. 10)"
          className="oem-input w-40"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Display name (e.g. Acme Hospital)"
          className="oem-input flex-1"
        />
        <button onClick={add} className="oem-button flex items-center gap-1.5">
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {entries.length > 0 ? (
        <div className="border border-oem-200 rounded-lg divide-y divide-oem-100">
          {entries.map(([id, alias]) => (
            <div key={id} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm text-oem-muted">{id}</span>
                <span className="text-oem-400">→</span>
                <span className="text-sm font-medium text-oem-900">{alias}</span>
              </div>
              <button
                onClick={() => { adminStore.removeAlias(id); onChanged(); }}
                className="p-1.5 rounded-lg hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors"
                title="Remove alias"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-oem-muted">No aliases defined yet.</p>
      )}
    </div>
  );
}

/* ── Activity Filters (exclusion rules) ── */

function ExclusionsTab({ events, onChanged }: { events: AnalyticsEvent[]; onChanged: () => void }) {
  const [kind, setKind] = useState<'user' | 'property'>('user');
  const [preset, setPreset] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [op, setOp] = useState<'equals' | 'contains'>('equals');
  const [value, setValue] = useState('');
  const rules = adminStore.getExclusions();

  const effectiveKey = preset === '__custom' ? customKey.trim() : preset;

  // Reuse the real matching engine so the "excludes N events" counts are exact.
  const excludedCount = (rule: ExclusionRule) =>
    events.length - applyExclusions(events, [rule]).length;

  const add = () => {
    if (!value.trim() || (kind === 'property' && !effectiveKey)) return;
    adminStore.addExclusion({
      kind,
      key: kind === 'property' ? effectiveKey : undefined,
      value: value.trim(),
      op,
    });
    setValue('');
    setCustomKey('');
    onChanged();
  };

  return (
    <div className="space-y-5">
      <p className="text-sm text-oem-muted">
        Filter out activity by user id, location, IP address, device, or any event property.
        Matching events are excluded from all reports, funnel, and search intelligence.
      </p>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value as 'user' | 'property')} className="oem-input w-44">
            <option value="user">User ID</option>
            <option value="property">Event property</option>
          </select>

          {kind === 'property' && (
            <select value={preset} onChange={(e) => setPreset(e.target.value)} className="oem-input w-52">
              <option value="">Select property…</option>
              {PROPERTY_PRESETS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.options.map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </optgroup>
              ))}
              <option value="__custom">Custom key…</option>
            </select>
          )}

          <select value={op} onChange={(e) => setOp(e.target.value as 'equals' | 'contains')} className="oem-input w-32">
            <option value="equals">is exactly</option>
            <option value="contains">contains</option>
          </select>
        </div>

        <div className="flex flex-wrap gap-2">
          {kind === 'property' && preset === '__custom' && (
            <input
              type="text"
              value={customKey}
              onChange={(e) => setCustomKey(e.target.value)}
              placeholder="Property key (e.g. $geoip_city)"
              className="oem-input w-56"
            />
          )}
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder={kind === 'user' ? 'User ID (e.g. 15)' : 'Value (e.g. United States, 203.0.113, TEST)'}
            className="oem-input flex-1 min-w-52"
          />
          <button onClick={add} className="oem-button flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      {rules.length > 0 ? (
        <div className="border border-oem-200 rounded-lg divide-y divide-oem-100">
          {rules.map(rule => (
            <div key={rule.id} className="flex items-center justify-between px-4 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-oem-900">
                  <span className="font-medium">{rule.kind === 'user' ? 'User ID' : rule.key}</span>
                  <span className="text-oem-400"> {rule.op === 'contains' ? 'contains' : '='} </span>
                  <span className="font-mono">{rule.value}</span>
                </p>
                <p className="text-xs text-oem-muted">
                  excludes {excludedCount(rule)} cached events · added {formatDate(rule.createdAt)}
                </p>
              </div>
              <button
                onClick={() => { adminStore.removeExclusion(rule.id); onChanged(); }}
                className="p-1.5 rounded-lg hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors shrink-0"
                title="Remove rule"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-oem-muted">No exclusion rules — all activity is included.</p>
      )}
    </div>
  );
}

/* ── Overrides ── */

function OverridesTab({ onChanged }: { onChanged: () => void }) {
  const fieldOverrides = db.getOverrides();
  const overrideOrders = db.getOverrideOrders();
  const deletedIds = db.getDeletedIds();

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-display text-base text-oem-navy mb-2">Field Overrides ({fieldOverrides.length})</h3>
        {fieldOverrides.length > 0 ? (
          <div className="border border-oem-200 rounded-lg overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-oem-50">
                  <th className="table-header">Order</th>
                  <th className="table-header">Field</th>
                  <th className="table-header">Value</th>
                  <th className="table-header">Updated</th>
                  <th className="table-header text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-oem-100">
                {fieldOverrides.map((o, i) => (
                  <tr key={i}>
                    <td className="table-cell font-mono text-xs">{o.orderId}</td>
                    <td className="table-cell">{String(o.field)}</td>
                    <td className="table-cell max-w-48 truncate">{JSON.stringify(o.value)}</td>
                    <td className="table-cell whitespace-nowrap text-xs">{formatDate(o.updatedAt)}</td>
                    <td className="table-cell text-right">
                      <button
                        onClick={() => { db.removeOverride(o.orderId, o.field); onChanged(); }}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors"
                        title="Remove override"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-oem-muted">No field-level overrides.</p>
        )}
      </section>

      <section>
        <h3 className="font-display text-base text-oem-navy mb-2">Overridden / Manual Orders ({overrideOrders.length})</h3>
        {overrideOrders.length > 0 ? (
          <div className="border border-oem-200 rounded-lg divide-y divide-oem-100">
            {overrideOrders.map(o => (
              <div key={o.id} className="flex items-center justify-between px-4 py-2.5">
                <div>
                  <p className="text-sm font-medium text-oem-900">{o.customerName}</p>
                  <p className="text-xs text-oem-muted font-mono">{o.id} · ${o.total.toLocaleString()} · updated {formatDate(o.updatedAt)}</p>
                </div>
                <button
                  onClick={() => { if (confirm('Delete this override order?')) { db.deleteOverrideOrder(o.id); onChanged(); } }}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors"
                  title="Delete override order"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-oem-muted">No overridden or manual orders.</p>
        )}
      </section>

      <section>
        <h3 className="font-display text-base text-oem-navy mb-2">Deleted Orders ({deletedIds.length})</h3>
        {deletedIds.length > 0 ? (
          <div className="border border-oem-200 rounded-lg divide-y divide-oem-100">
            {deletedIds.map(id => (
              <div key={id} className="flex items-center justify-between px-4 py-2.5">
                <span className="font-mono text-sm text-oem-700">{id}</span>
                <button
                  onClick={() => { db.restoreDeleted(id); onChanged(); }}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-status-automation hover:bg-status-automation/10 rounded-lg transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Restore
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-oem-muted">No deleted orders.</p>
        )}
      </section>
    </div>
  );
}

/* ── Raw Events ── */

function RawEventsTab({ events, onChanged }: { events: AnalyticsEvent[]; onChanged: () => void }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Local invalidation counter so the memo re-reads deleted ids / aliases
  // after a delete, independent of the parent refresh.
  const [localVersion, setLocalVersion] = useState(0);
  const pageSize = 50;

  const visible = useMemo(() => {
    void localVersion;
    const deletedIds = new Set(adminStore.getDeletedEventIds());
    const q = search.trim().toLowerCase();
    return events
      .filter(e => !deletedIds.has(e.id))
      .filter(e => {
        if (!q) return true;
        const customerId = e.properties?.customer_id != null ? String(e.properties.customer_id) : '';
        const quoteId = e.properties?.quote_id != null ? String(e.properties.quote_id) : '';
        return (
          e.event.toLowerCase().includes(q) ||
          e.distinct_id.toLowerCase().includes(q) ||
          customerId.includes(q) ||
          quoteId.includes(q)
        );
      })
      .slice()
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [events, search, localVersion]);

  const totalPages = Math.max(1, Math.ceil(visible.length / pageSize));
  const paginated = visible.slice((page - 1) * pageSize, page * pageSize);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const labelFor = (e: AnalyticsEvent): string => {
    const id = e.properties?.customer_id ?? e.distinct_id;
    const alias = adminStore.aliasFor(id);
    return alias ? `${alias} (${id})` : String(id);
  };

  const handleDeleteEvent = (e: AnalyticsEvent) => {
    if (confirm(`Delete this ${e.event} event? It will be excluded from all reports.`)) {
      adminStore.deleteEvent(e.id);
      setLocalVersion(v => v + 1);
      onChanged();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by event, user, or quote id…"
          className="oem-input flex-1"
        />
        <span className="text-xs text-oem-muted shrink-0">{visible.length} events</span>
      </div>

      <div className="border border-oem-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-oem-50">
              <th className="table-header">Time</th>
              <th className="table-header">Event</th>
              <th className="table-header">User</th>
              <th className="table-header">Quote</th>
              <th className="table-header text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-oem-100">
            {paginated.map(e => (
              <Fragment key={e.id}>
                <tr className={expanded.has(e.id) ? 'bg-oem-50' : ''}>
                  <td className="table-cell whitespace-nowrap text-xs">
                    <button
                      onClick={() => toggleExpanded(e.id)}
                      className="inline-flex items-center gap-1 hover:text-oem-800"
                      title={expanded.has(e.id) ? 'Collapse JSON' : 'Expand full JSON'}
                    >
                      {expanded.has(e.id) ? (
                        <ChevronDown className="w-3.5 h-3.5 text-accent-500" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-oem-400" />
                      )}
                      {formatDate(e.timestamp)}
                    </button>
                  </td>
                  <td className="table-cell">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      e.event === 'checkout_success' ? 'bg-status-automation/10 text-status-automation' :
                      e.event.startsWith('$') ? 'bg-oem-100 text-oem-600' :
                      'bg-accent-100 text-accent-700'
                    }`}>
                      {e.event}
                    </span>
                  </td>
                  <td className="table-cell text-sm">{labelFor(e)}</td>
                  <td className="table-cell font-mono text-xs">{e.properties?.quote_id ?? '—'}</td>
                  <td className="table-cell text-right">
                    <button
                      onClick={() => handleDeleteEvent(e)}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-oem-400 hover:text-red-600 transition-colors"
                      title="Delete event"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                {expanded.has(e.id) && (
                  <tr>
                    <td colSpan={5} className="p-3 bg-oem-50">
                      <JsonTree data={e} defaultOpenDepth={2} />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {paginated.length === 0 && (
              <tr><td className="table-cell text-center text-oem-muted py-6" colSpan={5}>No matching events.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-oem-muted">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-oem-200 text-sm text-oem-600 disabled:opacity-40 hover:bg-oem-50">Previous</button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 rounded-lg border border-oem-200 text-sm text-oem-600 disabled:opacity-40 hover:bg-oem-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Import / Export ── */

function BackupTab({ onChanged }: { onChanged: () => void }) {
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const handleExport = () => {
    const blob = new Blob([adminStore.exportData()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oemstream-admin-config-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setMessage({ ok: true, text: 'Config exported — share this file with other dashboard instances.' });
  };

  const handleImport = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    const result = adminStore.importData(text, mode);
    if (result.ok) {
      setMessage({ ok: true, text: `Imported: ${result.summary}` });
      onChanged();
    } else {
      setMessage({ ok: false, text: result.error || 'Import failed.' });
    }
  };

  const aliases = adminStore.getAliases();
  const exclusions = adminStore.getExclusions();
  const deletedIds = adminStore.getDeletedEventIds();
  const overrideOrdersCount = db.getOverrideOrders().length;
  const fieldOverridesCount = db.getOverrides().length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-oem-muted">
        Move admin configuration — user aliases, activity filter rules, deleted-event ids,
        manual/overridden orders, and field overrides — between dashboard instances
        so admins don't have to duplicate setup work.
      </p>

      <section className="border border-oem-200 rounded-lg p-4 space-y-3">
        <h3 className="font-display text-base text-oem-navy">Current Config</h3>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <div className="p-3 bg-oem-100 rounded-lg text-center">
            <p className="text-lg font-bold text-oem-navy">{Object.keys(aliases).length}</p>
            <p className="text-xs text-oem-muted">Aliases</p>
          </div>
          <div className="p-3 bg-oem-100 rounded-lg text-center">
            <p className="text-lg font-bold text-oem-navy">{exclusions.length}</p>
            <p className="text-xs text-oem-muted">Filter Rules</p>
          </div>
          <div className="p-3 bg-oem-100 rounded-lg text-center">
            <p className="text-lg font-bold text-oem-navy">{deletedIds.length}</p>
            <p className="text-xs text-oem-muted">Deleted Events</p>
          </div>
          <div className="p-3 bg-oem-100 rounded-lg text-center">
            <p className="text-lg font-bold text-oem-navy">{overrideOrdersCount}</p>
            <p className="text-xs text-oem-muted">Orders</p>
          </div>
          <div className="p-3 bg-oem-100 rounded-lg text-center">
            <p className="text-lg font-bold text-oem-navy">{fieldOverridesCount}</p>
            <p className="text-xs text-oem-muted">Field Overrides</p>
          </div>
        </div>
        <button onClick={handleExport} className="oem-button flex items-center gap-1.5">
          <Download className="w-4 h-4" />
          Export Config (.json)
        </button>
      </section>

      <section className="border border-oem-200 rounded-lg p-4 space-y-3">
        <h3 className="font-display text-base text-oem-navy">Import Config</h3>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-1.5 text-oem-700">
            <input type="radio" checked={mode === 'merge'} onChange={() => setMode('merge')} />
            Merge with existing
          </label>
          <label className="flex items-center gap-1.5 text-oem-700">
            <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} />
            Replace existing
          </label>
        </div>
        {mode === 'replace' && (
          <p className="text-xs text-status-intervention">
            Replace overwrites this instance's aliases, filter rules, and deleted-event ids entirely.
          </p>
        )}
        <label className="oem-button-outline inline-flex items-center gap-1.5 cursor-pointer w-fit">
          <Upload className="w-4 h-4" />
          Choose config file…
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => { handleImport(e.target.files?.[0]); e.target.value = ''; }}
          />
        </label>
      </section>

      {message && (
        <p className={`text-sm p-3 rounded-lg border ${
          message.ok
            ? 'bg-status-automation/10 border-status-automation/20 text-status-automation'
            : 'bg-status-escalation/10 border-status-escalation/20 text-status-escalation'
        }`}>
          {message.text}
        </p>
      )}
    </div>
  );
}
